import PropTypes from "prop-types";
import { childrenPropType, classNamePropType, onChangePropType, stylePropType } from "../../common";

/**
 * PropTypes for TabList component.
 *
 * @property {React.ReactNode} [children] - Tab elements to render inside the list
 * @property {number} activeTab - Zero-based index of the active tab
 * @property {Function} onClickTab - Click handler for tab selection
 * @property {Function} onKeyDownTab - Keyboard handler for arrow/Home/End navigation
 * @property {number} totalTabs - Total number of tabs
 * @property {string} tabBaseId - Base id used to link tabs and panels
 * @property {{current: Array}} tabRefs - Mutable ref array storing tab DOM nodes
 */
export const tabListPropTypes = {
  children: childrenPropType,
  activeTab: PropTypes.number.isRequired,
  onClickTab: PropTypes.func.isRequired,
  onKeyDownTab: PropTypes.func.isRequired,
  totalTabs: PropTypes.number.isRequired,
  tabBaseId: PropTypes.string.isRequired,
  tabRefs: PropTypes.shape({ current: PropTypes.array }).isRequired,
  className: classNamePropType,
  style: stylePropType,
};

/**
 * PropTypes for Tab component.
 *
 * @property {React.ReactNode} [children] - Tab label content
 * @property {boolean} isActive - Whether this tab is selected
 * @property {Function} onClick - Click handler
 * @property {Function} onKeyDown - Keyboard handler for navigation
 * @property {string} tabId - Unique tab id for aria-labelledby
 * @property {string} panelId - Associated panel id for aria-controls
 * @property {Function} tabRef - Ref callback storing the tab element
 */
export const tabPropTypes = {
  children: childrenPropType,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  tabId: PropTypes.string.isRequired,
  panelId: PropTypes.string.isRequired,
  tabRef: PropTypes.func.isRequired,
};

/**
 * PropTypes for TabPanel component.
 *
 * @property {React.ReactNode} [children] - Panel content
 * @property {number} activeTab - Zero-based active tab index
 * @property {string} tabBaseId - Base id used to link tabs/panels
 */
export const tabPanelPropTypes = {
  children: childrenPropType,
  activeTab: PropTypes.number.isRequired,
  tabBaseId: PropTypes.string.isRequired,
};

/**
 * PropTypes for Panel component (tab content wrapper).
 *
 * @property {React.ReactNode} [children] - Content to render inside the panel
 * @property {Object} [style] - Inline style overrides
 * @property {string} [className] - Optional className for styling
 */
export const panelPropTypes = {
  children: childrenPropType,
  style: PropTypes.object,
  className: classNamePropType,
};

/**
 * PropTypes for Tabs root component.
 *
 * @property {React.ReactNode} [children] - TabList and TabPanel components
 * @property {Function} [onChange] - Called when active tab changes (zero-based index)
 * @property {number} [selected] - Controlled active tab (1-based to preserve legacy API)
 * @property {string} [id] - Optional id base for tab/panel linking
 * @property {string} [className] - Optional className for container
 */
export const tabsRootPropTypes = {
  children: childrenPropType,
  onChange: onChangePropType,
  selected: PropTypes.number,
  id: PropTypes.string,
  className: classNamePropType,
};
